/** Computers get the QR since they cannot open store links; touch devices get the store badges. */

import React, { CSSProperties } from "react";
import useIsMobile from "../../customHooks/useIsMobile";
import usePrefersQr from "../../customHooks/usePrefersQr";
import useAppPlacement from "../../customHooks/useAppPlacement";
import { APP_PLACEMENTS, AppPlacementId } from "../../utils/appPlacements";
import * as translator from "../../utils/translator";
import appStoreBadge from "../../images/appInstall/app-store-badge.png";
import googlePlayBadge from "../../images/appInstall/google-play-badge.png";
import { FrameBox, FrameText, getBannerFrame } from "./frames";
import QrCode from "./QrCode";
import "./AppInstallBanner.css";

export interface AppInstallBannerProps {
  desktopPlacementId: AppPlacementId;
  mobilePlacementId: AppPlacementId;
  language: string;
  /** Country from the URL; gated on country, not language, so de-CH must not match. */
  country?: string | null;
  customerUuid?: string | null;
  /** Undefined while loading, so the banner stays hidden until pairing is known. */
  isPairedToMobileApp?: boolean;
  /** Placement ids the api allows. Undefined while loading, which keeps the banner hidden. */
  allowedPlacements?: readonly string[];
  /** No longer part of the app link; kept so callers need no change. */
  orderHash?: string | null;
  /** Borrows another placement's art; copy, gating, tracking and the link stay on the placement id. */
  designPlacementId?: AppPlacementId;
  /** False when another surface already counted this view, like the W6 row before its popup. */
  countView?: boolean;
  className?: string;
}

/** A length in Figma frame pixels; `--u` is one frame pixel at the rendered width. */
const u = (n: number) => `calc(${n} * var(--u))`;

const boxStyle = (b: Omit<FrameBox, "height"> & { height?: number; radius?: number }): CSSProperties => ({
  left: u(b.left),
  top: u(b.top),
  width: u(b.width),
  height: b.height === undefined ? undefined : u(b.height),
  borderRadius: b.radius === undefined ? undefined : u(b.radius),
});

const textStyle = (b: FrameText): CSSProperties => ({
  ...boxStyle(b),
  fontSize: u(b.fontSize),
  lineHeight: u(b.lineHeight),
});

/** The badge link holds only images, so its click reports a fixed label. */
const BADGES_CTA_TEXT = "store_badges";

const AppInstallBanner = ({
  desktopPlacementId,
  mobilePlacementId,
  language,
  country,
  customerUuid,
  isPairedToMobileApp,
  allowedPlacements,
  orderHash,
  designPlacementId,
  countView = true,
  className = "",
}: AppInstallBannerProps) => {
  const isNarrow = useIsMobile();
  // Without matchMedia, fall back to the width: QR wide, badges narrow.
  const prefersQr = usePrefersQr() ?? !isNarrow;
  const placementId = isNarrow ? mobilePlacementId : desktopPlacementId;
  const config = APP_PLACEMENTS[placementId];
  const frame = getBannerFrame(designPlacementId ?? placementId, isNarrow, prefersQr);

  const { visible, href, qrValue, onClick, ref } = useAppPlacement({
    placementId,
    country,
    customerUuid,
    isPairedToMobileApp,
    allowedPlacements,
    orderHash,
    inView: countView,
    cta: frame?.badges ? "badges" : "qr",
    ctaText: BADGES_CTA_TEXT,
  });

  if (!visible || !frame) return null;

  const t = (key: string) =>
    translator.translate(language, config.translationCategory, `${config.translationKeyPrefix}/${key}`);

  return (
    <section
      ref={ref}
      className={`app-install-banner ${isNarrow ? "is-mobile" : "is-desktop"} ${className}`.trim()}
      data-frame={frame.id}
      data-testid="app-install-banner"
      data-placement={placementId}
      aria-label={t("headline")}
      style={{ maxWidth: `${frame.width}px` }}
    >
      <div
        className="app-install-banner__canvas"
        style={{ "--frame-w": frame.width, aspectRatio: `${frame.width} / ${frame.height}` } as CSSProperties}
      >
        <img className="app-install-banner__art" src={frame.image} alt="" aria-hidden="true" />

        <p className="app-install-banner__eyebrow" data-testid="app-install-eyebrow">
          <span className="app-install-banner__pill" style={{ ...boxStyle(frame.pill), fontSize: u(frame.pill.fontSize) }}>
            <span className="app-install-banner__pill-text">{t("eyebrow")}</span>
          </span>
          <span className="app-install-banner__brand" style={textStyle(frame.brand)}>
            {t("brand")}
          </span>
        </p>

        <h2 className="app-install-banner__headline" data-testid="app-install-headline" style={textStyle(frame.headline)}>
          {t("headline")}
        </h2>

        {frame.body && (
          <p className="app-install-banner__body" data-testid="app-install-body" style={textStyle(frame.body)}>
            {t("body")}
          </p>
        )}

        {frame.badges ? (
          <a
            className="app-install-banner__cta app-install-banner__badges"
            data-testid="app-install-cta"
            href={href}
            onClick={onClick}
            style={boxStyle(frame.cta)}
          >
            <span className="app-install-banner__badge-row" data-testid="store-badges">
              <img
                data-testid="store-badge-ios"
                className="app-install-banner__badge"
                src={appStoreBadge}
                alt={t("app_store_alt")}
                style={boxStyle(frame.badges[0])}
              />
              <img
                data-testid="store-badge-android"
                className="app-install-banner__badge"
                src={googlePlayBadge}
                alt={t("google_play_alt")}
                style={boxStyle(frame.badges[1])}
              />
            </span>
          </a>
        ) : (
          // Scan only: the QR is for a phone camera, so the panel is not a link.
          <div
            className="app-install-banner__cta app-install-banner__qr-panel"
            data-testid="app-install-qr-panel"
            style={boxStyle(frame.cta)}
          >
            {frame.qrTitle && (
              <span className="app-install-banner__qr-title" style={textStyle(frame.qrTitle)}>
                {t("qr_title")}
              </span>
            )}
            {frame.qrSubtitle && (
              <span className="app-install-banner__qr-subtitle" style={textStyle(frame.qrSubtitle)}>
                {t("qr_subtitle")}
              </span>
            )}
            {frame.qrPlate && frame.qr && (
              <span className="app-install-banner__qr-code" style={boxStyle(frame.qrPlate)}>
                <span
                  data-testid="qr"
                  className="app-install-banner__qr-img"
                  style={boxStyle({ left: frame.qr.left, top: frame.qr.top, width: frame.qr.size, height: frame.qr.size })}
                >
                  {qrValue && <QrCode value={qrValue} />}
                </span>
              </span>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default AppInstallBanner;
